import React, { useRef, useState } from "react";
import { columnsConfig } from "./flotteColumns.config";
import specialFiltersConfig from "./specialFilters.config";
import useFlotteFilters from "../useFlotteFilters";
import FilterPopover from "./FilterPopover";
import useStore from "../../../../../context/useStore";
import { useAtom } from "jotai";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import { isVORole } from "./FlotteUtils";
import tools from "../../../../../helpers/tools";

/**
 * Build a lookup: filterKey -> { columnKey, title, filterType, placeholder, options }
 */
const getFilterableColumns = () => {
  const result = {};
  Object.entries(columnsConfig).forEach(([columnKey, cfg]) => {
    if (cfg.filterable && cfg.filterable !== false) {
      const { filterKey, filterType, placeholder, options } = cfg.filterable;
      result[filterKey] = {
        columnKey,
        title: cfg.title,
        filterType,
        placeholder,
        optionsPath: options,
      };
    }
  });
  return result;
};

const filterableColumns = getFilterableColumns();

/**
 * Format a filter value for display inside a badge
 */
const formatFilterValue = (value, filterType, resolveOptions) => {
  if (value === null || value === undefined || value === "") return null;

  switch (filterType) {
    case "text":
      return `"${value}"`;

    case "tag-input":
      if (Array.isArray(value) && value.length > 0) {
        return value.join(", ");
      }
      return null;

    case "select": {
      if (!value) return null;
      const opts = resolveOptions?.() || [];
      const match = opts.find(
        (o) => (typeof o === "object" ? `${o.id}` : `${o}`) === `${value}`,
      );
      return match ? (typeof match === "object" ? match.name : match) : value;
    }

    case "select-multiple": {
      if (!Array.isArray(value) || value.length === 0) return null;
      const opts = resolveOptions?.() || [];
      const labels = value.map((v) => {
        const match = opts.find(
          (o) => (typeof o === "object" ? `${o.id}` : `${o}`) === `${v}`,
        );
        return match ? (typeof match === "object" ? match.name : match) : v;
      });
      return labels.join(", ");
    }

    case "date-range": {
      if (!value || typeof value !== "object") return null;
      const from = value.from ? tools.formatDate(value.from) : null;
      const to = value.to ? tools.formatDate(value.to) : null;
      if (from && to) return `${from} → ${to}`;
      if (from) return `à partir du ${from}`;
      if (to) return `jusqu'au ${to}`;
      return null;
    }

    default:
      return String(value);
  }
};

/**
 * Check if a filter value is "active" (non-empty)
 */
const isFilterActive = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Boolean(value.from || value.to);
  }
  return true;
};

const ActiveFilterBadge = ({
  filterKey,
  title,
  displayValue,
  filterType,
  currentValue,
  placeholder,
  resolveOptions,
  onFilterChange,
  onFilterClear,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const badgeRef = useRef(null);

  return (
    <>
      <span
        ref={badgeRef}
        className="badge d-inline-flex align-items-center"
        style={{
          backgroundColor: "#e8f0fe",
          color: "#1a73e8",
          fontSize: "0.8em",
          fontWeight: 500,
          padding: "4px 8px",
          borderRadius: 12,
          cursor: "pointer",
          gap: 6,
          whiteSpace: "nowrap",
          maxWidth: 300,
          border: "1px solid #c5d9f7",
        }}
        onClick={() => setIsPopoverOpen(true)}
        title={`${title}: ${displayValue}`}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 220,
          }}
        >
          <strong>{title}:</strong> {displayValue}
        </span>
        <button
          type="button"
          className="btn btn-link p-0 m-0 border-0 shadow-none"
          style={{
            fontSize: "0.85em",
            lineHeight: 1,
            color: "#1a73e8",
            minWidth: 14,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onFilterClear(filterKey);
          }}
          title={`Supprimer le filtre ${title}`}
        >
          <i className="fas fa-times"></i>
        </button>
      </span>

      <FilterPopover
        isOpen={isPopoverOpen}
        onClose={() => setIsPopoverOpen(false)}
        onApply={(value) => onFilterChange(filterKey, value)}
        onClear={() => onFilterClear(filterKey)}
        filterType={filterType}
        currentValue={currentValue}
        options={resolveOptions?.()}
        placeholder={placeholder}
        buttonRef={badgeRef}
        label={`Filtrer par ${title.toLowerCase()}`}
      />
    </>
  );
};

const SpecialFilterBadge = ({ filterKey, title, icon, onClear }) => {
  return (
    <span
      className="badge d-inline-flex align-items-center"
      style={{
        backgroundColor: "#fef3e2",
        color: "#e67e22",
        fontSize: "0.8em",
        fontWeight: 500,
        padding: "4px 8px",
        borderRadius: 12,
        gap: 6,
        whiteSpace: "nowrap",
        maxWidth: 300,
        border: "1px solid #f5d9a8",
      }}
      title={title}
    >
      {icon && <i className={`fas ${icon}`} style={{ fontSize: "0.85em" }}></i>}
      <strong>{title}</strong>
      <button
        type="button"
        className="btn btn-link p-0 m-0 border-0 shadow-none"
        style={{
          fontSize: "0.85em",
          lineHeight: 1,
          color: "#e67e22",
          minWidth: 14,
        }}
        onClick={() => onClear(filterKey)}
        title={`Supprimer le filtre ${title}`}
      >
        <i className="fas fa-times"></i>
      </button>
    </span>
  );
};

const ActiveFilters = () => {
  const [connectedUser] = useAtom(connectedUserAtom);
  const [state] = useStore();
  const {
    flotteFilters,
    handleChangeFilters,
    handleClearFilter,
    onResetSearch,
  } = useFlotteFilters();

  const getFilterOptions = (optionsPath) => {
    if (!optionsPath) return undefined;
    if (optionsPath === "state.constant.items.FLOTTE.STATUTS_VEHICULE") {
      if (isVORole(connectedUser?.role)) {
        return state.constant.items.FLOTTE.STATUTS_VEHICULE_VO;
      }
      return state.constant.items.FLOTTE.STATUTS_VEHICULE;
    }
    if (optionsPath === "state.constant.items.CATALOG_HITECH") {
      return state?.constant?.items?.CATALOG_HITECH;
    }
    if (optionsPath === "state.constant.items.FLOTTE.CARBURANTS") {
      return state?.constant?.items?.FLOTTE.CARBURANTS;
    }
    return undefined;
  };

  const handleFilterChange = (filterKey, value) => {
    handleChangeFilters({
      target: { name: filterKey, value, type: "filter" },
    });
  };

  // Collect active column filters
  const activeFilters = Object.entries(flotteFilters)
    .filter(([key]) => key !== "page" && filterableColumns[key] !== undefined)
    .filter(([, value]) => isFilterActive(value))
    .map(([filterKey, value]) => {
      const col = filterableColumns[filterKey];
      const resolveOptions = () => getFilterOptions(col.optionsPath);
      const displayValue = formatFilterValue(
        value,
        col.filterType,
        resolveOptions,
      );
      if (!displayValue) return null;
      return {
        filterKey,
        title: col.title,
        displayValue,
        filterType: col.filterType,
        currentValue: value,
        placeholder: col.placeholder,
        resolveOptions,
      };
    })
    .filter(Boolean);

  // Collect active special filters
  const activeSpecialFilters = specialFiltersConfig.filter((f) =>
    Boolean(flotteFilters[f.filterKey]),
  );

  if (activeFilters.length === 0 && activeSpecialFilters.length === 0)
    return null;

  return (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: 4 }}>
      {activeSpecialFilters.map((filter) => (
        <SpecialFilterBadge
          key={filter.filterKey}
          filterKey={filter.filterKey}
          title={filter.title}
          icon={filter.icon}
          onClear={handleClearFilter}
        />
      ))}
      {activeFilters.map((filter) => (
        <ActiveFilterBadge
          key={filter.filterKey}
          {...filter}
          onFilterChange={handleFilterChange}
          onFilterClear={handleClearFilter}
        />
      ))}
      <button
        type="button"
        className="btn btn-link p-0 m-0 border-0 shadow-none"
        style={{
          fontSize: "0.8em",
          color: "#dc3545",
          whiteSpace: "nowrap",
        }}
        onClick={onResetSearch}
        title="Effacer tous les filtres"
      >
        <i className="fas fa-times-circle mr-1"></i>
        Vider
      </button>
    </div>
  );
};

export default ActiveFilters;
